import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Button,
	ConfirmationDialog,
	ConfirmationDialogRef,
	SelectField,
	WidgetInputTextField,
	WidgetLabel,
} from '../../../rapid-ui'
import { SettingsSubheading } from '../shared'
import { CompanyDataRetentionPolicyViewModel } from './company-data-retention-policy.vm'

export interface CompanyDataRetentionPolicyProps {
	viewModel: CompanyDataRetentionPolicyViewModel
}
export function CompanyDataRetentionPolicy({ viewModel }: CompanyDataRetentionPolicyProps) {
	const { t } = useTranslation('common')
	const deleteConfirmationRef = useRef<ConfirmationDialogRef>(null)

	const handleDelete = async () => {
		const candidate = viewModel.view.candidateSearchResult
		if (!candidate) { return }

		try {
			await deleteConfirmationRef.current?.confirm({
				title: t('settings.dataRetention.deleteConfirmTitle'),
				message: t('settings.dataRetention.deleteConfirmMessage', {
					name: candidate.name || candidate.email,
				}),
				confirmText: t('settings.dataRetention.delete'),
				cancelText: t('common.cancel'),
			})
			viewModel.events.onCandidateDelete()
		} catch {
			// User cancelled
		}
	}

	return (
		<section className="space-y-6">
			<ConfirmationDialog ref={deleteConfirmationRef} />
			<SettingsSubheading>{t('settings.dataRetention.title')}</SettingsSubheading>
			<p>
				{t('settings.dataRetention.description')}
			</p>
			<div>
				<SelectField
					options={viewModel.view.policyRetentionPeriodOptionsInMonths}
					value={viewModel.view.policyRetentionPeriodInMonths}
					onChange={viewModel.events.onPolicyRetentionPeriodChange}
					label={t('settings.dataRetention.retentionPeriodLabel')}
					errors={[viewModel.errors.policyRetentionPeriodError]}
					hint={t('settings.dataRetention.retentionPeriodHint')}
					renderOption={(months) => t('settings.dataRetention.monthsOption', { count: months })}
				/>
			</div>

			<SettingsSubheading>{t('settings.dataRetention.deletionTitle')}</SettingsSubheading>
			<p>
				{t('settings.dataRetention.deletionDescription')}
			</p>
			<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
				<div className="font-medium text-black -900 mb-1">
					{t('settings.dataRetention.irreversibleWarning')}
				</div>
				<div className="text-grey-700">
					<p>
						{t('settings.dataRetention.irreversibleDescription')}
					</p>
				</div>
			</div>

			<div className="space-y-4">
				<div className="flex flex-col gap-y-4">
					<WidgetLabel label={t('settings.dataRetention.candidateEmailLabel')}>
						<WidgetInputTextField
							errors={[viewModel.errors.candidateSearchError]}
							value={viewModel.view.candidateSearchQuery}
							hint={t('settings.dataRetention.candidateEmailHint')}
							onChange={(e) => viewModel.events.onCandidateSearchQueryChange(e.trim())}
						/>
					</WidgetLabel>
					<Button
						type="committal"
						size="short"
						onClick={viewModel.events.onCandidateSearch}
						isLoading={viewModel.view.isSearching}
						disabled={!viewModel.view.candidateSearchQuery}
					>
						{t('settings.dataRetention.search')}
					</Button>
				</div>

				{viewModel.view.candidateSearchResult && (
					<div className="rounded-md border border-grey-200 p-4 space-y-3">
						<div className="flex justify-between items-start">
							<div>
								<p className="ph-no-capture font-medium">
									{viewModel.view.candidateSearchResult.name || t('settings.dataRetention.noName')}
								</p>
								<p className="ph-no-capture text-sm text-grey-500">
									{viewModel.view.candidateSearchResult.email}
								</p>
								<p className={'text-sm text-grey-500'}>
									{t('settings.dataRetention.applicationsToDelete', {
										count: viewModel.view.candidateSearchResult.applicationsCount,
									})}
								</p>
							</div>
							<Button
								type="destructive"
								size="short"
								onClick={handleDelete}
								isLoading={viewModel.view.isDeleting}
							>
								{t('settings.dataRetention.delete')}
							</Button>
						</div>
						{viewModel.errors.candidateDeleteError && (
							<p className="text-sm text-red-600">{viewModel.errors.candidateDeleteError}</p>
						)}
					</div>
				)}
			</div>
		</section>
	)
}
